import { inject, Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  CanActivateFn,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import { jwtDecode } from "jwt-decode";
import { catchError, map, Observable, of } from "rxjs";
import { AuthenticationService } from "./authentication.service";
import { MagicLinkService } from "./magic-link.service";
import { RolesService } from "./roles.service";
import { NavigationService } from "./navigation.service";

@Injectable({
  providedIn: "root",
})
class PermissionsService {
  constructor(
    private router: Router,
    private authenticationService: AuthenticationService,
  ) {}

  isAdmin(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
    return this.authenticationService.isAdmin.value;
  }
}

export const AdminGuard: CanActivateFn = (
  next: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
): boolean => {
  return inject(PermissionsService).isAdmin(next, state);
};

export const SuperadminGuard: CanActivateFn = (): Observable<boolean | ReturnType<Router["createUrlTree"]>> => {
  const router = inject(Router);
  const rolesService = inject(RolesService);
  const fallback = router.createUrlTree(["/", NavigationService.ADMINPAGE]);

  return rolesService.me().pipe(
    map((me) => (me.superadmin ? true : fallback)),
    catchError(() => of(fallback)),
  );
};

export const MagicOrAdminGuard: CanActivateFn = (route, state) => {
  const router = inject(Router);
  const auth = inject(AuthenticationService);
  const magicService = inject(MagicLinkService);

  try {
    const isAdmin =
      (auth as any).isAdmin && typeof (auth as any).isAdmin === "object"
        ? (auth as any).isAdmin.value
        : (auth as any).isAdmin;
    if (isAdmin) {
      return true;
    }

    const qp = (route && (route as any).queryParams) || {};
    let token = qp["magicLinkToken"] || magicService.getTokenFromStorage();

    if (!token) {
      return false;
    }

    const payload: any = jwtDecode(token);
    if (!payload) {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    if (!payload.exp || typeof payload.exp !== "number" || payload.exp < now) {
      console.warn("Magic token expired or invalid exp", payload);
      return false;
    }

    const routeId =
      (route.params && route.params["id"]) ||
      (route.paramMap && route.paramMap.get("id"));
    if (!routeId || String(payload.evaluationId) !== String(routeId)) {
      console.warn(
        "Magic token evaluationId mismatch",
        payload?.evaluationId,
        routeId,
      );
      return false;
    }

    magicService.setMagic(token, payload as any);

    return true;
  } catch (err) {
    console.error("MagicOrAdminGuard error:", err);
    return false;
  }
};
